import {
  ARCHIVE_FILE_TYPES,
  FILE_EXT_TO_MIME,
  MIME_TYPES,
} from '@/src/io/mimeTypes';
import { getMimeTypeFromFilename } from '@/src/io/io';
import type { DataSource, FileSource } from '@/src/io/import/dataSource';
import { isNiftiFileName, NIFTI_MIME } from '@/src/io/import/niftiImportIntent';

const MESH_MIMES = new Set<string>([
  FILE_EXT_TO_MIME.stl,
  FILE_EXT_TO_MIME.vtp,
]);

/** Image formats that always import as a primary volume (not NIfTI — intent is chosen later). */
const VOLUME_IMAGE_MIMES = new Set<string>([
  FILE_EXT_TO_MIME.dcm,
  FILE_EXT_TO_MIME.nrrd,
  FILE_EXT_TO_MIME.nhdr,
  FILE_EXT_TO_MIME.mha,
  FILE_EXT_TO_MIME.mhd,
  FILE_EXT_TO_MIME.vti,
  FILE_EXT_TO_MIME.gipl,
  'application/vnd.unknown.gipl',
  FILE_EXT_TO_MIME.mnc,
  FILE_EXT_TO_MIME.mnc2,
  FILE_EXT_TO_MIME.mgh,
  FILE_EXT_TO_MIME.mrc,
  FILE_EXT_TO_MIME.vtk,
  FILE_EXT_TO_MIME.jpg,
  FILE_EXT_TO_MIME.jpeg,
  FILE_EXT_TO_MIME.png,
  FILE_EXT_TO_MIME.tif,
  FILE_EXT_TO_MIME.tiff,
]);

function resolveFileMime(source: FileSource): string {
  const { fileType, file } = source;
  if (fileType && MIME_TYPES.has(fileType)) {
    return fileType;
  }
  return getMimeTypeFromFilename(file.name) ?? fileType;
}

function isSegmentationFileName(name: string): boolean {
  return name.toLowerCase().includes('.segmentation.');
}

function isMeshMime(mime: string): boolean {
  return MESH_MIMES.has(mime);
}

function isVolumeImageMime(mime: string): boolean {
  return VOLUME_IMAGE_MIMES.has(mime) || ARCHIVE_FILE_TYPES.has(mime);
}

/**
 * Extensionless files are often DICOM instances opened from a folder picker.
 */
function isLikelyDicomInstance(source: FileSource): boolean {
  const name = source.file.name;
  if (name.includes('.')) return false;
  const mime = resolveFileMime(source);
  return mime === '' || mime === FILE_EXT_TO_MIME.dcm;
}

function isNiftiSource(source: FileSource): boolean {
  const mime = resolveFileMime(source);
  return mime === NIFTI_MIME || isNiftiFileName(source.file.name);
}

function inspectFileSource(
  source: FileSource
): 'volume' | 'overlay' | 'unknown' | 'nifti' {
  if (isNiftiSource(source)) {
    return 'nifti';
  }

  const name = source.file.name;
  if (isSegmentationFileName(name)) {
    return 'overlay';
  }

  const mime = resolveFileMime(source);
  if (isMeshMime(mime)) {
    return 'overlay';
  }
  if (isVolumeImageMime(mime) || isLikelyDicomInstance(source)) {
    return 'volume';
  }
  return 'unknown';
}

function collectFileSources(sources: DataSource[]): FileSource[] {
  const files: FileSource[] = [];
  for (const source of sources) {
    if (source.type === 'file') {
      files.push(source);
    } else if (source.type === 'archive' && source.parent.type === 'file') {
      files.push(source.parent);
    } else if (source.type === 'collection') {
      files.push(...collectFileSources(source.sources));
    }
  }
  return files;
}

function sourcesIncludeRemoteVolume(sources: DataSource[]): boolean {
  return sources.some((source) => {
    if (source.type === 'uri') return true;
    if (source.type === 'collection') {
      return sourcesIncludeRemoteVolume(source.sources);
    }
    return false;
  });
}

/**
 * Returns true when the incoming sources include a new primary scan (volume),
 * as opposed to overlay-only data such as meshes or name-matched segmentations.
 *
 * NIfTI is intentionally excluded: volume vs segmentation is resolved later
 * in {@link importSingleFile} after the NIfTI intent dialog.
 */
export function sourcesIncludeVolumeLoad(sources: DataSource[]): boolean {
  if (sourcesIncludeRemoteVolume(sources)) return true;

  const files = collectFileSources(sources);
  if (!files.length) return false;

  let sawUnknown = false;
  let sawNifti = false;

  for (const fileSource of files) {
    const kind = inspectFileSource(fileSource);
    if (kind === 'volume') return true;
    if (kind === 'unknown') sawUnknown = true;
    if (kind === 'nifti') sawNifti = true;
  }

  if (sawUnknown) return true;

  // NIfTI-only batches defer volume vs segmentation to importSingleFile.
  if (sawNifti) return false;

  return false;
}
